import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import { KeyboardDatePicker } from '@material-ui/pickers'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import InputAdornment from '@material-ui/core/InputAdornment'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import Button from '@material-ui/core/Button'
import Icon from '@material-ui/core/Icon'
import SaveIcon from '@material-ui/icons/Save'
import Grid from '@material-ui/core/Grid'

import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { DatePicker } from '@material-ui/pickers'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { useAuth } from '@graniot/common/context/AuthContext'
import { format } from 'date-fns'

import { enhancedReducer } from '../../../../utils'
import { useAddCropMutation } from '../../../../adapters/crops'
import { useDeleteCropMutation } from '../../../../adapters/crops'
import { useUpdateCropMutation } from '../../../../adapters/crops'
import yara from '../../../assets/cnp/yara-small-logo.svg'
import { useParcelCNPFormData } from '../../../../adapters/cnp'
import CenteredProgress from '@graniot/common/utils/centeredprogress'

const useStyles = makeStyles((theme) => ({
	prgList: {
		minHeight: '100%',
	},
}))

let initialState = {
	yield_unit: null,
	objective_yield: null,
	solid_product_applied: null,
	soil_type: null,
	harvested_qty: 0,
	sowing: null,
	closure: null,
	campaign: '',
}

function formReducer(state, action) {
	switch (action.type) {
		case 'UPDATE_FIELD':
			return { ...state, [action.field]: action.value }
		case 'UPDATE_DATE':
			return {
				...state,
				[action.field]: format(action.value, 'yyyy-MM-dd'),
			}
		case 'RESET_FORM':
			return initialState
		case 'SET_INITIAL_STATE':
			return { ...action.payload }
		default:
			return state
	}
}

export default function FormCNP({ crop, parcel, setCreateCNP }) {
	const classes = useStyles()
	const { user } = useAuth()

	const { t } = useTranslation()
	const { isLoading, data, error, isFetching, refetch } =
		useParcelCNPFormData(user, parcel)

	const insertModel = useAddCropMutation()
	const updateModel = useUpdateCropMutation()
	const [state, dispatch] = React.useReducer(
		formReducer,
		crop ? crop : { ...initialState, parcel: parcel.id }
	)

	const handleTextChange = ({ target: { name, value } }) => {
		dispatch({
			type: 'UPDATE_FIELD',
			field: name,
			value: value,
		})
	}

    const handleAutocompleteChange = React.useCallback((event, newValue) => {
		console.log(event, newValue)
		dispatch({
			type: 'UPDATE_FIELD',
			field: newValue._path,
			value: newValue._value,
		})
    }, []);

	const handleDateChange = (date, field) => {
		dispatch({ type: 'UPDATE_DATE', field, value: date })
	}

	const handleSubmit = () => {
		// Add validation logic if needed
		if (crop) {
			updateModel.mutate(state) // Assuming this function updates a crop
		} else {
			insertModel.mutate({ ...state, parcel: parcel.id }) // Assuming this function adds a new crop
		}
	}

	if (isLoading) return <CenteredProgress />

	return (
		<Grid container spacing={2}>
			<Grid item xs={6}>
				<IconButton
					aria-label="delete"
					className={classes.margin}
					size="small"
					onClick={() => setCreateCNP(false)}
				>
					<ArrowBackIcon fontSize="inherit" />
				</IconButton>
			</Grid>
			<Grid item xs={6} style={{ textAlign: 'right' }}>
				<img src={yara} style={{ width: 50 }} />
			</Grid>

			<Grid item xs={12}>
				<Typography variant="h5">Datos del cultivo</Typography>
			</Grid>
			<Grid item xs={10}>
				<TextField
					label="Rendimiento objetivo"
					name="objective_yield"
					value={state.objective_yield}
					onChange={handleTextChange}
					fullWidth
					size="small"
				/>
			</Grid>
			<Grid item xs={2}>
				<Autocomplete
					id="yield-unit"
					name="yield_unit"
					value={state.yield_unit}
					onChange={handleAutocompleteChange}
					options={data ? data?.yield_units : []}
					getOptionLabel={(option) => option['name']}
					fullWidth
					size="small"
					renderInput={(params) => (
						<TextField {...params}  label="Yield unit" />
					)}
				/>
			</Grid>
			<Grid item xs={12}>
				<Autocomplete
					id="Qué manejo haces en tu olivar"
					options={
						data
							? data?.crop_regions[0].additionalProperties[0][
									'Options'
							  ]
							: []
					}
					getOptionLabel={(option) => option['Name']}
					fullWidth
					size="small"
					value={state.solid_product_applied}
					onChange={handleTextChange}
					renderInput={(params) => (
						<TextField
							{...params}
							label="¿Qué manejo haces en tu olivar?"
							name="solid_product_applied"
						/>
					)}
				/>
			</Grid>

			<Grid item xs={12}>
				<Typography variant="h5">Datos del suelo</Typography>
			</Grid>
			<Grid item xs={12}>
				<Autocomplete
					id="Tipo de suelo"
					value={state.soil_type}
					options={[]}
					getOptionLabel={(option) => option['Name']}
					fullWidth
					size="small"
					renderInput={(params) => (
						<TextField {...params} 
						label="Tipo de suelo"
						name="soil_type" />
					)}
				/>
			</Grid>
			<Grid item xs={12}>
				<Autocomplete
					id="Valores de nutrientes del análisis del suelo"
					options={[]}
					getOptionLabel={(option) => option['Name']}
					fullWidth
					size="small"
					renderInput={(params) => (
						<TextField
							{...params}
							label="Valores de nutrientes del análisis del suelo"
						/>
					)}
				/>
			</Grid>

			<Grid item xs={12}>
				<Typography variant="h5">Fertilizante orgánico</Typography>
			</Grid>
			<Grid item xs={6}>
				<TextField
					label={'Producto'}
					name="variety"
					onChange={handleTextChange}
					fullWidth
					size="small"
					required
				/>
			</Grid>

			<Grid item xs={4}>
				<TextField
					label={'Cantidad por unidad de demanda'}
					name="harvested_qty_expected"
					value={state.harvested_qty_expected}
					onChange={handleTextChange}
					type="number"
					fullWidth
					size="small"
				/>
			</Grid>
			<Grid item xs={2}>
				<Autocomplete
					id="demand-unit"
					options={data ? data?.demand_units : []}
					getOptionLabel={(option) => option['name']}
					fullWidth
					size="small"
					renderInput={(params) => (
						<TextField {...params} label="Demand unit" />
					)}
				/>
			</Grid>

			<Grid item xs={12}>
				<TextField
					label={'Nombre de la recomendación'}
					name="variety"
					value={state.variety}
					onChange={handleTextChange}
					fullWidth
					size="small"
					required
				/>
			</Grid>
			<Grid
				item
				xs={12}
				container
				direction="row"
				justifyContent="space-between"
				alignItems="center"
			>
				<Button
					color="primary"
					size="small"
					variant="contained"
					style={{ width: '100%' }}
				>
					Crear un plan de nutrición
				</Button>
			</Grid>
			<Grid
				item
				xs={12}
				container
				direction="row"
				justifyContent="space-between"
				alignItems="center"
			>
				<Button
					color="primary"
					size="small"
					style={{ width: '100%' }}
					onClick={() => setCreateCNP(false)}
				>
					Cancelar
				</Button>
			</Grid>
		</Grid>
	)
}
